import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { documentsAndMessagesKeys } from "../api/documentsAndMessagesKeys";
import type { DocumentsAndMessagesResponse } from "../api/types";
import { subscribeCoord } from "./channel";

/**
 * Opener-side listener for the viewer's `doc-action-applied` message. The
 * viewer window calls the shared `docs/{docId}/actions` endpoint for check-in,
 * move-to-approval, and move-to-state, and broadcasts on every success; this
 * hook patches the opener's docs & messages list to match, then invalidates so
 * the list resyncs with the server once the backend has settled:
 *
 * - Moved to a different approval number: the document is removed from this
 *   deal's list immediately (it's no longer valid for this deal). The document's
 *   own window closes itself (see `DocumentViewerPage`'s move-success handling)
 *   — there's no shared "focused document" selection to clear anymore now that
 *   each document has its own window.
 * - Checked in: the document's `reviewedBy` is optimistically set to the
 *   acting analyst's oraid (carried on the message, since the BFF's action
 *   response has no document fields to read it from).
 * - Moved to a different state only: no local list shape changes (the
 *   document stays in this deal), so only the trailing invalidate applies.
 *
 * The patches are applied directly to the cache (not just an invalidate +
 * refetch) because the BFF's own propagation can lag behind the mutation's
 * response — an immediate refetch would otherwise race that and return stale
 * data. `invalidateQueries` still runs after, so the list eventually resyncs
 * (message/unread counts, the server's authoritative `reviewedBy`, etc.) once
 * the backend has settled.
 * @param {string} approvalId - The deal / approval identifier this Review page is showing.
 */
export function useDocumentActionListener(approvalId: string): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribe = subscribeCoord((message) => {
      if (message.type !== "doc-action-applied" || message.appId !== approvalId) {
        return;
      }

      const movedToOtherApproval = message.request.moveToApprovalNumber !== undefined;

      const queryKey = documentsAndMessagesKeys.detail({ approvalId });
      queryClient.setQueryData<DocumentsAndMessagesResponse>(queryKey, (previous) => {
        if (!previous) {
          return previous;
        }

        if (movedToOtherApproval) {
          const documents = previous.data.documents.filter((document) => document.docId !== message.docId);
          if (documents.length === previous.data.documents.length) {
            return previous;
          }
          const documentCount = Math.max(0, previous.data.documentCount - 1);
          return { ...previous, data: { ...previous.data, documents, documentCount } };
        }

        if (message.reviewedBy) {
          let changed = false;
          const documents = previous.data.documents.map((document) => {
            if (document.docId !== message.docId || document.reviewedBy) {
              return document;
            }
            changed = true;
            return { ...document, reviewedBy: message.reviewedBy };
          });
          if (!changed) {
            return previous;
          }
          return { ...previous, data: { ...previous.data, documents } };
        }

        return previous;
      });

      void queryClient.invalidateQueries({ queryKey });
    });
    return unsubscribe;
  }, [approvalId, queryClient]);
}
